import type { NavConfirmation } from "~/helpers/aiChat/fernieActions";
import type { IntakeDraft } from "~/helpers/aiChat/fernieIntake";
import type { StreamStep } from "~/helpers/aiChat/streamSteps";
import type {
	AnswerLayoutResult,
	AnswerRating,
	ArtifactResult,
	PriceImportApply,
	PriceImportCard,
	ProposalState,
	ToolCallStatus
} from "~/services/aiOrchestrator";

import applyArtifactBudget from "~/helpers/aiChat/persistArtifacts";

export type ProposalCardState = ProposalState | "FAILED" | "OUTCOME_UNKNOWN";

export type ToolResultStatus = ToolCallStatus | "UNKNOWN";

export interface ToolResult {
	detail?: string;
	status: ToolResultStatus;
	summary?: string;
	toolName: string;
}

export interface UnavailableArtifact {
	filename: string;
	mimeType: string;
}

export interface MutationProposal {
	argumentsJson: string;
	cancelNote?: string;
	id: string;
	priceImport?: PriceImportCard;
	priceImportApply?: PriceImportApply;
	resultSummary?: string;
	state: ProposalCardState;
	summary: string;
	toolName: string;
}

export interface Message {
	cancelled?: boolean;
	chosenNavPath?: string;
	content: string;
	fileName?: string;
	htmlArtifacts?: string[];
	id?: string;
	intakeDrafts?: IntakeDraft[];
	layouts?: AnswerLayoutResult[];
	navConfirmation?: NavConfirmation;
	proposals?: MutationProposal[];
	rating?: AnswerRating;
	reportArtifacts?: ArtifactResult[];
	role: "assistant" | "user";
	serverId?: string;
	steps?: StreamStep[];
	streaming?: boolean;
	toolResults?: ToolResult[];
	unavailableReportArtifacts?: UnavailableArtifact[];
}

export interface SavedChat {
	createdAt: string;
	id: string;
	messages: Message[];
	sessionId: string | null;
	title: string;
	updatedAt: string;
}

const STORAGE_KEY = "worksuite-ai-chats";
const MAX_CHATS = 50;

const normalizeMessage = (message: Message & { toolsUsed?: string[] }): Message => {
	if (message.toolResults || !message.toolsUsed) return message;
	const { toolsUsed, ...rest } = message;
	return { ...rest, toolResults: toolsUsed.map((toolName) => ({ status: "UNKNOWN", toolName })) };
};

const settleCommittingPriceImport = (proposal: MutationProposal): MutationProposal =>
	proposal.state === "COMMITTING" && proposal.priceImport ? { ...proposal, state: "OUTCOME_UNKNOWN" } : proposal;

const settleCommittingPriceImports = (message: Message): Message =>
	message.proposals ? { ...message, proposals: message.proposals.map(settleCommittingPriceImport) } : message;

const loadSaved = (): SavedChat[] => {
	try {
		const chats: SavedChat[] = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
		return chats.map((chat) => ({
			...chat,
			messages: chat.messages.map((message) => settleCommittingPriceImports(normalizeMessage(message)))
		}));
	} catch {
		return [];
	}
};

const stripPendingNavConfirmation = (message: Message): Message => {
	if (!message.navConfirmation || message.chosenNavPath !== undefined) return message;
	const { navConfirmation, ...rest } = message;
	return rest;
};

const stripPendingIntakeDrafts = (message: Message): Message => {
	if (!message.intakeDrafts) return message;
	const resolved = message.intakeDrafts.filter((draft) => draft.resolution !== undefined);
	if (resolved.length === message.intakeDrafts.length) return message;
	if (resolved.length === 0) {
		const { intakeDrafts, ...rest } = message;
		return rest;
	}
	return { ...message, intakeDrafts: resolved };
};

const stripStreamingState = (message: Message): Message => {
	if (!message.steps && !message.streaming) return message;
	const { steps, streaming, ...rest } = message;
	return rest;
};

const stripTransientArtifacts = (message: Message): Message =>
	stripStreamingState(stripPendingIntakeDrafts(stripPendingNavConfirmation(message)));

const stripTransientChats = (chats: SavedChat[]): SavedChat[] =>
	chats.map((chat) => ({ ...chat, messages: chat.messages.map(stripTransientArtifacts) }));

const writeChats = (chats: SavedChat[]) => localStorage.setItem(STORAGE_KEY, JSON.stringify(chats));

const historyListeners = new Set<() => void>();

const subscribeHistory = (listener: () => void): (() => void) => {
	historyListeners.add(listener);
	return () => {
		historyListeners.delete(listener);
	};
};

const notifyHistory = () => queueMicrotask(() => historyListeners.forEach((listener) => listener()));

const hasHistory = (): boolean => {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (!raw) return false;
		const chats = JSON.parse(raw);
		return Array.isArray(chats) && chats.length > 0;
	} catch {
		return false;
	}
};

const persistSaved = (chats: SavedChat[]) => {
	const stripped = stripTransientChats(chats.slice(0, MAX_CHATS));
	try {
		writeChats(applyArtifactBudget(stripped));
	} catch {
		try {
			writeChats(applyArtifactBudget(stripped, 0));
		} catch {
			void 0;
		}
	}
	notifyHistory();
};

const deriveChatTitle = (messages: Message[]) =>
	messages.find((m) => m.role === "user")?.content.slice(0, 60) || "New Chat";

const buildUpdatedChat = (
	prev: SavedChat[],
	chatId: string,
	messages: Message[],
	sessionId: string | null
): SavedChat => {
	const now = new Date().toISOString();
	const idx = prev.findIndex((c) => c.id === chatId);
	return {
		createdAt: idx >= 0 ? prev[idx].createdAt : now,
		id: chatId,
		messages,
		sessionId,
		title: deriveChatTitle(messages),
		updatedAt: now
	};
};

const mergeChat = (prev: SavedChat[], chat: SavedChat): SavedChat[] => {
	const idx = prev.findIndex((c) => c.id === chat.id);
	return idx >= 0 ? prev.map((c) => (c.id === chat.id ? chat : c)) : [chat, ...prev];
};

const upsertSavedChat = (
	prev: SavedChat[],
	chatId: string,
	messages: Message[],
	sessionId: string | null
): SavedChat[] => {
	const chat = buildUpdatedChat(prev, chatId, messages, sessionId);
	const updated = mergeChat(prev, chat);
	persistSaved(updated);
	return updated;
};

const chatStorage = {
	hasHistory,
	loadSaved,
	MAX_CHATS,
	persistSaved,
	STORAGE_KEY,
	subscribeHistory,
	upsertSavedChat
};

export default chatStorage;
